import { queryClient } from '@/providers/AllProviders';
import { useNavigate, useParams } from 'react-router-dom';
import { MetaApiResponse } from 'utils/types/CommonApiResponse';
import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import {
  createBusinessPlan,
  deleteBusinessPlan,
  getAgencyPlan,
  getBusinessPlan,
  getBusinessPlans,
  updateBusinessPlan
} from './api';
import { OrganizationPackageResponse, PackageResponse } from './types';

export function useGetBusinessPackages(
  params?: Record<string, any>,
  options?: UseQueryOptions<{ data: PackageResponse[]; meta: MetaApiResponse }, Error>
) {
  return useQuery<{ data: PackageResponse[]; meta: MetaApiResponse }>({
    queryKey: ['business_packages'],
    queryFn: () => getBusinessPlans(params),
    ...options
  });
}

export function useGetBusinessPackage(id?: string, options?: UseQueryOptions<PackageResponse, Error>) {
  const { packageId } = useParams<{ packageId: string }>();

  return useQuery<PackageResponse>({
    queryKey: ['business_package', packageId, id],
    queryFn: () => getBusinessPlan(packageId ?? id!),
    enabled: Boolean(packageId || id),
    ...options
  });
}

export function useGetAgencyPackage(id?: number, options?: UseQueryOptions<OrganizationPackageResponse, Error>) {
  return useQuery<OrganizationPackageResponse>({
    queryKey: ['agency_package'],
    queryFn: () => getAgencyPlan(id!),
    enabled: Boolean(id),
    ...options
  });
}

export function useCreateOrUpdateBusinessPlan() {
  const navigate = useNavigate();
  const { packageId } = useParams<{ packageId: string }>();

  return useMutation({
    mutationFn: (payload: any) => (packageId ? updateBusinessPlan(packageId, payload) : createBusinessPlan(payload)),
    onSuccess: (data) => {
      if (data !== undefined) {
        queryClient.invalidateQueries({
          queryKey: ['business_packages'],
          refetchType: 'active'
        });
        queryClient.refetchQueries({
          queryKey: ['business_package']
        });
        navigate(-1);
      }
    }
  });
}

export function useDeleteBusinessPlan() {
  return useMutation({
    mutationFn: (id: string) => deleteBusinessPlan(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['business_packages'],
        refetchType: 'active'
      });
    }
  });
}
